import React, { useState, useMemo } from "react"
import { useDispatch } from "react-redux"
import { styled } from "@mui/material/styles"
import Rating from "@mui/material/Rating"
import TextField from "@mui/material/TextField"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Grid from "@mui/material/Grid"
import CloseIcon from "@mui/icons-material/Close"
import { useMutation, useQuery } from "@apollo/client"
import Box from "@mui/system/Box/Box"
import { setSnackbar } from "../../../actions/globalUiActions"
import { updateUserOption } from "../../../actions/userActions"

import { formatDate } from "../../../utils/dateUtils"
import { useSelectorTyped } from "../../../store/configureStore"
import {
  CreateReviewDocument,
  GQLStudentLessonForReviewsFragmentFragment,
  StudentLessonsForReviewsDocument,
} from "./ReviewsGql.graphqlTypes"
import { GENERIC_GOOGLE_REVIEW_URL } from "../../../config"

const StyledRating = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconFilled": {
    color: theme.palette.accentTwo.main,
  },
  "& .MuiRating-iconEmpty": {
    color: theme.palette.accentTwo.main,
  },
}))

type ReviewFormState = {
  rating: number
  review: string
}

const StudentReviewAlert = () => {
  const { auth } = useSelectorTyped((state) => state.firebase)
  const { userOptions } = useSelectorTyped((state) => state.user)
  const dispatch = useDispatch()
  const [reviewFormState, setReviewFormState] = useState<ReviewFormState>({
    rating: 0,
    review: "",
  })

  const timeValues = useMemo(() => {
    const currentTime = new Date().valueOf()
    const sixtyDaysAgo = currentTime - 60 * 60 * 24 * 60 * 1000
    return { currentTime, sixtyDaysAgo }
  }, [])

  const { data: lessonData } = useQuery(StudentLessonsForReviewsDocument, {
    variables: {
      studentId: auth?.uid || "",
      endTimeBefore: timeValues.currentTime,
      startTimeAfter: timeValues.sixtyDaysAgo,
    },
    skip: !auth?.uid,
  })

  const studentLessons:
    | GQLStudentLessonForReviewsFragmentFragment[]
    | undefined = lessonData?.lessons.edges
    .map((edge) => edge.node)
    .filter(
      (lesson) =>
        !lesson.studentDetailsList.some((student) => student.hasReview),
    )
  const singleLesson = studentLessons?.[0]

  const [createReviewMutation, reviewDocumentResult] = useMutation(
    CreateReviewDocument,
    {
      onError: () => {
        dispatch(setSnackbar("Could not save this review.", "error"))
      },
    },
  )

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!singleLesson || !auth?.uid) {
      // Just a type check.
      dispatch(
        setSnackbar(
          "Could not save this review - no lesson or signed out",
          "error",
        ),
      )
      return
    }

    createReviewMutation({
      variables: {
        input: {
          instructorId: singleLesson.instructorId,
          lessonId: singleLesson.id,
          studentId: auth.uid,
          rating: reviewFormState.rating,
          text: reviewFormState.review,
        },
      },
    })
  }

  const callToAction = () => {
    if (!singleLesson) {
      // Just a type check.
      return null
    }

    return (
      <Box sx={{ p: 2 }}>
        <h3 style={{ marginTop: 0 }}>
          How would you rate your lesson with {singleLesson.instructor.name} on{" "}
          {formatDate(
            singleLesson.startTime,
            singleLesson.instructor.timeZone,
            "long",
          )}
          ?
        </h3>
        <Box sx={{ maxWidth: 600, margin: "0 auto", textAlign: "left" }}>
          <form onSubmit={handleSubmit}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item>
                <Box display="flex" justifyContent="center" alignItems="center">
                  <StyledRating
                    name="reviewRating"
                    value={reviewFormState.rating}
                    onChange={(_event, newValue) =>
                      setReviewFormState({
                        ...reviewFormState,
                        rating: newValue || 5,
                      })
                    }
                    size="large"
                  />
                </Box>
              </Grid>
              {reviewFormState.rating ? (
                <>
                  <Grid item>
                    <TextField
                      variant="outlined"
                      label="Add a brief review?"
                      value={reviewFormState.review}
                      onChange={(event) =>
                        setReviewFormState({
                          ...reviewFormState,
                          review: event.target.value,
                        })
                      }
                      name="review"
                      margin="dense"
                      fullWidth
                      multiline
                      color="secondary"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "white",
                          },
                          "&:hover fieldset": {
                            borderColor: "white",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "white",
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: "white",
                        },
                        "& .MuiInputLabel-root": {
                          color: "white",
                        },
                        "& .MuiInputLabel-shrink": {
                          color: "white",
                        },
                        width: "100%",
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <Button variant="contained" type="submit" color="secondary">
                      Submit Rating
                    </Button>
                  </Grid>
                </>
              ) : null}
            </Grid>
          </form>
        </Box>
      </Box>
    )
  }

  const googleReviewCTA = () => {
    return (
      <h4 style={{ margin: 0 }}>
        Thank you for your rating. Want to spread the word about Coastline?
        Please consider leaving a review on{" "}
        <a
          href={GENERIC_GOOGLE_REVIEW_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "white" }}
        >
          Google Reviews
        </a>
        . Have a great day!
      </h4>
    )
  }

  if (!auth?.uid || !singleLesson || userOptions.hideReview) {
    return <span data-cy="review-cta-alert-hidden" />
  }

  return (
    <Box
      sx={{ p: 2, textAlign: "center" }}
      data-cy="review-cta-alert"
      className="section-highlight"
    >
      <Box
        sx={{
          float: "right",
          marginTop: reviewDocumentResult.data?.createReview ? -2 : 0,
        }}
      >
        <IconButton
          onClick={() => dispatch(updateUserOption({ hideReview: true }))}
          data-cy="review-cta-close"
          size="large"
          sx={{ color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      {reviewDocumentResult.data?.createReview
        ? googleReviewCTA()
        : callToAction()}
    </Box>
  )
}

export default StudentReviewAlert
